import dayjs from 'dayjs';

import {removeEmptyKeysFromObject} from '@polarsignals/functions';

import {BlogPostMeta} from 'types/blog';
import {getBlogHomepagePosts, getTag, getAuthor} from './sanity.client';

export const slugify = str => {
  return str.toLowerCase().replace(/[^a-zA-Z0-9]+/g, '-');
};

function importAll(r) {
  return r
    .keys()
    .filter(e => e.startsWith('./posts/'))
    .map(fileName => ({
      link: fileName.substr(1).replace(/\/index\.mdx$/, ''),
      module: r(fileName),
    }));
}

export const removeInvalidTags = (tags: string[]) => {
  return tags.filter(tag => tag !== null);
};

export const getAllMarkdownPosts: () => Promise<BlogPostMeta[]> = async () => {
  return await Promise.all(
    importAll(require.context('../pages/blog/', true, /\.mdx$/, 'lazy'))
      .reverse()
      .map(async ({link, module}) => {
        const {meta} = await module;
        return removeEmptyKeysFromObject<BlogPostMeta, BlogPostMeta>({
          link,
          title: meta.title,
          tags: meta.tags,
          description: meta.description,
          date: meta.date,
          authors: meta.authors,
          authorLinks: meta.authorLinks,
          pageAuthorLinks: meta.authors.map(author => slugify(author)),
          image: meta.image,
          tweetLink: meta.tweetLink,
          hnLink: meta.hnLink,
          isMarkdown: true,
        });
      })
  );
};

export const getAllPostsFromSanityAndMarkdown = async () => {
  const [sanityPosts, markdownPosts = []] = await Promise.all([
    getBlogHomepagePosts(),
    getAllMarkdownPosts(),
  ]);

  const markdownPostsAndSanityPosts = [...markdownPosts, ...sanityPosts];

  const sortedByDatePosts = markdownPostsAndSanityPosts.sort((a, b) => {
    return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
  });

  return sortedByDatePosts;
};

export const getAllTagPosts = async (tag: string) => {
  const sanityContent = await getTag(tag);

  // Safely return an empty array if there are no posts in Sanity
  const getSanityContent = () => {
    if (sanityContent?.posts) {
      sanityContent.posts = sanityContent.posts.map(post => ({
        date: dayjs(post.publishedAt).format('MMMM D, YYYY'),
        ...post,
      }));
      return sanityContent;
    }

    return [];
  };

  const sanityTag = getSanityContent();

  const filteredMarkdownPosts = (await getAllMarkdownPosts())
    .map(post => ({
      ...post,
      tags: post.tags.map(tag => tag.toLowerCase().replace(/-/g, ' ')), // Tranform tags to lowercase and replace dashes with spaces i.e. "Data Science" instead of "data-science"
      isMarkdown: true,
    }))
    .filter(post => post.tags.includes(tag.toLowerCase().replace(/-/g, ' ')));

  // If there are no posts in Sanity, return only the markdown posts
  const allTagContent = sanityTag.posts
    ? {
        title: sanityTag.title,
        posts: [...filteredMarkdownPosts, ...sanityTag.posts].sort((a, b) => {
          return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
        }),
      }
    : {
        title: tag.replace(/-/g, ' ').replace(/^\w/, c => c.toUpperCase()),
        posts: filteredMarkdownPosts,
      };

  return allTagContent;
};

export const getAllAuthorPosts = async (author: string) => {
  const filteredMarkdownPosts = (await getAllMarkdownPosts())
    .map(post => ({
      ...post,
      isMarkdown: true,
    }))
    .filter(post => post.pageAuthorLinks.includes(author));

  const sanityAuthor = await getAuthor(author);

  sanityAuthor.posts = sanityAuthor.posts.map(post => ({
    date: dayjs(post.publishedAt).format('MMMM D, YYYY'),
    ...post,
  }));

  sanityAuthor.posts = [...filteredMarkdownPosts, ...sanityAuthor.posts].sort((a, b) => {
    return dayjs(b.date).valueOf() - dayjs(a.date).valueOf();
  });

  return sanityAuthor;
};

export const getSimilarPosts = async (meta: BlogPostMeta) => {
  const {tags, title} = meta;
  const allPosts = await getAllPostsFromSanityAndMarkdown();
  const similarPosts: BlogPostMeta[] = allPosts
    .map(post => {
      return {
        link: post.link,
        title: post.title,
        similarityScore: tags.filter(tag => post.tags.includes(tag)).length,
      };
    })
    .filter(post => post.title !== title)
    .sort((a, b) => b.similarityScore - a.similarityScore)
    .slice(0, 3)
    .map(({link}) => allPosts.find(post => post.link === link) as BlogPostMeta);
  return similarPosts;
};

export const getArticleSlug = (slug: string, date: string): string => {
  const articleDate = dayjs(date).format('YYYY/MM/DD');
  return `/posts/${articleDate}/${slug}`;
};

export const isArticleDateValid = (dateFromURL: string, publishedDate: string): boolean => {
  const articleDate = dayjs(dateFromURL).format('YYYY/MM/DD');
  const publishedDateFormatted = dayjs(publishedDate).format('YYYY/MM/DD');
  return articleDate === publishedDateFormatted;
};
